import { AirspaceNotamType, AirspaceRestrictionCategory, AirspaceTfrType } from "@nova/constants"
import { AirspaceState } from "tools/store/localStorageRegistry"

export type CategoryConfig<C extends keyof AirspaceState, Key extends string = string> = {
    label: string
    category: C
    sections: {
        label: string
        subKey?: string
        options: { key: Key; label: string; description: string }[]
    }[]
}

export const restrictions: CategoryConfig<"restrictions", AirspaceRestrictionCategory> = {
    label: "Restrictions",
    category: "restrictions",
    sections: [
        {
            label: "United States",
            subKey: "usa",
            options: [
                {
                    key: "B",
                    label: "B",
                    description:
                        "Controlled airspace around the busiest airports (major hubs). ATC authorization required to enter.",
                },
                {
                    key: "C",
                    label: "C",
                    description:
                        "Controlled airspace around airports with an operating control tower and radar approach control. ATC authorization required.",
                },
                {
                    key: "D",
                    label: "D",
                    description:
                        "Controlled airspace around smaller airports with an operating control tower. Two-way radio contact with ATC required.",
                },
                {
                    key: "MOA",
                    label: "MOA",
                    description:
                        "Military Operations Area: airspace where military training (including high-speed and aerobatic maneuvers) may be underway. Not restricted to civil traffic, but caution advised.",
                },
                {
                    key: "Restricted",
                    label: "Restricted",
                    description:
                        "Airspace where flight may be hazardous to aircraft (e.g. artillery fire, guided missiles). Entry requires permission from the controlling authority.",
                },
                {
                    key: "Prohibited",
                    label: "Prohibited",
                    description:
                        "Airspace where flight is entirely forbidden, typically for national security or safety reasons.",
                },
                {
                    key: "Other",
                    label: "OTHER",
                    description:
                        "An airspace restriction that doesn't fall into a standard FAA category.",
                },
            ],
        },
        {
            label: "Canada",
            subKey: "canada",
            options: [
                {
                    key: "awyLf",
                    label: "AWY LF",
                    description:
                        "A low-frequency airway: a designated route defined by low/medium-frequency (NDB) navigation aids.",
                },
                {
                    key: "awyVhf",
                    label: "AWY VHF",
                    description:
                        "A VHF (high-altitude) airway: a designated route defined by VHF navigation aids.",
                },
                {
                    key: "CAE",
                    label: "CAE",
                    description:
                        "Control Area Extension: controlled airspace extending beyond a terminal control area to protect specific routes or procedures.",
                },
                {
                    key: "CYA",
                    label: "CYA",
                    description:
                        "Canadian Advisory Area: activity of interest to pilots (e.g. parachuting, gliding) occurs here; no entry restriction.",
                },
                {
                    key: "CYD",
                    label: "CYD",
                    description:
                        "Canadian Danger Area: activities (e.g. military exercises) may pose a hazard to aircraft; not legally restricted but caution advised.",
                },
                {
                    key: "CYR",
                    label: "CYR",
                    description:
                        "Canadian Restricted Area: entry is restricted; permission from the controlling agency is required.",
                },
                {
                    key: "CZ",
                    label: "CZ",
                    description:
                        "Control Zone: controlled airspace around an airport, normally extending from the surface up to a specified altitude.",
                },
                {
                    key: "rnavRt",
                    label: "RNAV RT",
                    description:
                        "An RNAV route: a designated route defined by satellite/area navigation rather than ground-based aids.",
                },
                {
                    key: "TA",
                    label: "TA",
                    description:
                        "Terminal Area: controlled airspace serving a smaller airport or group of airports.",
                },
                {
                    key: "TCA",
                    label: "TCA",
                    description:
                        "Terminal Control Area: controlled airspace around major airports handling high traffic volumes.",
                },
                {
                    key: "tspRq",
                    label: "TSP RQ",
                    description:
                        "Transponder-required airspace: aircraft must carry and operate a transponder to enter.",
                },
                {
                    key: "rest",
                    label: "OTHER",
                    description:
                        "An airspace restriction that doesn't fall into a standard NAV Canada category.",
                },
            ],
        },
    ],
}

export const tfrs: CategoryConfig<"tfrs", AirspaceTfrType> = {
    label: "TFRs",
    category: "tfrs",
    sections: [
        {
            label: "TYPE",
            options: [
                {
                    key: "AS",
                    label: "AS",
                    description:
                        "Air Shows/Sports: temporary restriction to protect spectators and participants at an air show or sporting event.",
                },
                {
                    key: "HAZ",
                    label: "HAZ",
                    description:
                        "Hazards: temporary restriction around a hazard such as a wildfire, disaster response, or other emergency operation.",
                },
                {
                    key: "SEC",
                    label: "SEC",
                    description:
                        "Security: temporary restriction for security reasons (e.g. protecting a sensitive site or event).",
                },
                {
                    key: "spcOps",
                    label: "SPC OPS",
                    description:
                        "Space Operations: temporary restriction to support a rocket launch or other space-related activity.",
                },
                {
                    key: "SPEC",
                    label: "SPEC",
                    description:
                        "Special Security Instructions: temporary restriction issued under special national-security directives.",
                },
                {
                    key: "uasPg",
                    label: "UAS PG",
                    description:
                        "UAS Public Gathering: temporary restriction limiting drone (UAS) operations over a large public gathering.",
                },
                {
                    key: "VIP",
                    label: "VIP",
                    description:
                        "VIP Movement: temporary restriction protecting the movement of the President, Vice President, or other VIP.",
                },
            ],
        },
    ],
}

export const notams: CategoryConfig<"notams", AirspaceNotamType> = {
    label: "NOTAMs",
    category: "notams",
    sections: [
        {
            label: "NOTAMs",
            options: [
                {
                    key: "IAP",
                    label: "IAP",
                    description:
                        "Instrument Approach Procedure: a notice affecting an instrument approach procedure.",
                },
                {
                    key: "LGT",
                    label: "LGT",
                    description:
                        "Lighting: a notice about airport or airfield lighting being out of service or changed.",
                },
                {
                    key: "NAV",
                    label: "NAV",
                    description:
                        "Navigation Aid: a notice about a navigation aid being out of service or changed.",
                },
                {
                    key: "OBST",
                    label: "OBST",
                    description:
                        "Obstacle: a notice about a new or changed obstacle (e.g. a crane or tower) affecting the airspace.",
                },
                {
                    key: "OBST_LGT",
                    label: "OBST LGT",
                    description:
                        "Obstacle Lighting: a notice specifically about an obstacle's lighting being out of service or changed.",
                },
                {
                    key: "RWY",
                    label: "RWY",
                    description:
                        "Runway: a notice affecting a runway (closure, condition change, markings, etc.).",
                },
                {
                    key: "TXY",
                    label: "TXY",
                    description:
                        "Taxiway: a notice affecting a taxiway (closure, condition change, etc.).",
                },
                {
                    key: "OTHER",
                    label: "OTHER",
                    description: "A notice that doesn't fall into a standard NOTAM category.",
                },
            ],
        },
    ],
}
